import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../helpers/AuthContext';
import AweChart from '../AweChart';
import './StorefrontAnalytics.css';

const RANGE_OPTIONS = [
    { value: 7, label: 'Last 7 days' },
    { value: 30, label: 'Last 30 days' },
    { value: 90, label: 'Last 90 days' },
    { value: 365, label: 'Last 365 days' },
];

/**
 * The five legacy `analytics.ejs` sections, each an `AweChart` over a filtered event slice.
 * `productPath` maps the selected product uuid to the `path` value that section's events carry.
 * `redHidden` sections (marketplace / mobile-app impressions) never fire for Mayhem Red channels.
 */
const SECTIONS = [
    {
        id: 'pageviews',
        title: 'Storefront Pageviews',
        blurb: 'The number of times individual storefront pages were viewed by browsers with JavaScript (probably humans).',
        match: event => event.type === 'pageview',
        productPath: uuid => `/store/detail/${uuid}`,
    },
    {
        id: 'ls-session',
        title: 'Storefront Unique Browsers',
        blurb: 'Each event is a web browser our tracker has never seen before — think "brand-new shopper".',
        match: event => event.type === 'ls-session',
        productPath: uuid => `/store/detail/${uuid}`,
    },
    {
        id: 'w-impression',
        title: 'Marketplace Product Impressions',
        blurb: 'Each time your product was seen on the mayhem.my marketplace.',
        match: event => event.type === 'w-impression',
        productPath: uuid => uuid,
        redHidden: true,
    },
    {
        id: 'm-impression',
        title: 'Mobile App Product Impressions',
        blurb: 'Each time your product was seen in the Mayhem Marketplace app.',
        match: event => event.type === 'm-impression',
        productPath: uuid => uuid,
        redHidden: true,
    },
    {
        id: 'add-to-cart',
        title: 'Add to Cart Actions',
        blurb: 'Successful add-to-cart actions from the web and the mobile app.',
        match: event => event.type === 'add-to-cart-web' || event.type === 'add-to-cart-mobile',
        productPath: uuid => `/product/${uuid}`,
    },
];

function sectionEvents(section, events, productUuid) {
    return events.filter(event => {
        if (!section.match(event)) return false;
        if (!productUuid) return true;
        return event.path === section.productPath(productUuid);
    });
}

export default function StorefrontAnalytics({ isRed = false, selectedProductUuid = null }) {
    const auth = useAuth();
    const [days, setDays] = useState(7);
    const [productUuid, setProductUuid] = useState(selectedProductUuid || '');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        auth.fetch(`${auth.API_URL}/v1/seller/analytics?days=${days}`)
            .then(r => r.json())
            .then(json => {
                if (cancelled) return;
                if (json.meta?.success) {
                    setData(json.data);
                } else {
                    setError('Failed to load analytics.');
                }
            })
            .catch(() => {
                if (!cancelled) setError('Failed to load analytics.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [auth.API_URL, days]);

    const sections = useMemo(
        () => SECTIONS.filter(section => !(section.redHidden && isRed)),
        [isRed],
    );

    const events = data?.events || [];
    const products = data?.products || [];
    const storefrontUrl = data?.storefrontUrl || '';

    return (
        <div className="storefront-analytics">
            <div className="storefront-analytics__controls">
                <label>
                    <span>Date range</span>
                    <select
                        value={days}
                        onChange={e => setDays(Number(e.target.value))}
                    >
                        {RANGE_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </label>
                <label>
                    <span>Filter by product</span>
                    <select
                        value={productUuid}
                        onChange={e => setProductUuid(e.target.value)}
                        disabled={loading || !!error}
                    >
                        <option value="">All products</option>
                        {products.map(product => (
                            <option key={product.uuid} value={product.uuid}>{product.name}</option>
                        ))}
                    </select>
                </label>
            </div>

            {error && <div className="storefront-analytics__state flash error">{error}</div>}
            {!error && loading && (
                <div className="storefront-analytics__state" role="status">Loading analytics…</div>
            )}

            {!error && !loading && sections.map(section => {
                const filtered = sectionEvents(section, events, productUuid);
                return (
                    <section key={section.id} className="storefront-analytics__section">
                        <h2>{section.title}</h2>
                        <p className="storefront-analytics__blurb">{section.blurb}</p>
                        {filtered.length === 0 ? (
                            <p className="storefront-analytics__empty">No data in this range.</p>
                        ) : (
                            <AweChart
                                events={filtered}
                                products={products}
                                storefrontUrl={storefrontUrl}
                            />
                        )}
                    </section>
                );
            })}
        </div>
    );
}
